import { Button, FormField, Input, PageContainer, PageHeader, PageHeaderActionPrimary, PageHeaderActions, PageHeaderActionSecondary, PageHeaderLeading, PageHeaderStack, Skeleton, useDeclareUiSurface } from '@yeshuman/sdk'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'


import { BusinessModelCanvas } from './business-model-canvas'
import { StubCanvas } from './stub-canvas'
import { useDeleteCanvas, useUpdateCanvas, useWorkshopCanvas } from './use-canvases'

export function CanvasEditorPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { data: canvas, isLoading, isError, refetch } = useWorkshopCanvas(id)
  const updateCanvas = useUpdateCanvas(id ?? '')
  const deleteCanvas = useDeleteCanvas()

  const [title, setTitle] = useState('')
  const [blocks, setBlocks] = useState<Record<string, string[]>>({})

  useDeclareUiSurface(
    id
      ? { intent: 'edit', entity: 'workshop_canvas', id, mode: 'page', fields: ['title', 'blocks'] }
      : null,
  )

  useEffect(() => {
    if (!canvas) return
    setTitle(canvas.title)
    setBlocks(canvas.blocks)
  }, [canvas])

  function changeBlock(blockId: string, items: string[]) {
    setBlocks((prev) => ({ ...prev, [blockId]: items }))
  }

  async function handleSave() {
    if (!id) return
    await updateCanvas.mutateAsync({ title, blocks })
    toast.success('Saved')
  }

  async function handleDelete() {
    if (!id) return
    await deleteCanvas.mutateAsync(id)
    toast.success('Deleted')
    navigate('/canvases')
  }

  if (!id) {
    return <Navigate to="/canvases" replace />
  }

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="mb-6 h-10 w-48" />
        <Skeleton className="h-96 w-full" />
      </PageContainer>
    )
  }

  if (isError || !canvas) {
    return (
      <PageContainer>
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-sm text-muted-foreground">This canvas could not be opened.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Try again
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/canvases">Back to canvases</Link>
          </Button>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeaderStack>
        <PageHeader
          title={title.trim() || 'Untitled canvas'}
          description={
            canvas.template.stub
              ? `${canvas.type_label} · stub template`
              : canvas.type_label
          }
          leading={<PageHeaderLeading back={{ label: 'Canvases', to: '/canvases' }} />}
          actions={
            <PageHeaderActions
              secondary={
                <PageHeaderActionSecondary
                  onClick={() => void handleDelete()}
                  disabled={deleteCanvas.isPending}
                >
                  Delete
                </PageHeaderActionSecondary>
              }
              primary={
                <PageHeaderActionPrimary onClick={() => void handleSave()} disabled={updateCanvas.isPending}>
                  {updateCanvas.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                  Save
                </PageHeaderActionPrimary>
              }
            />
          }
        />
      </PageHeaderStack>

      <div className="mb-4 max-w-xl">
        <FormField label="Title" htmlFor="workshop-canvas-title">
          <Input
            id="workshop-canvas-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Untitled canvas"
          />
        </FormField>
      </div>

      {canvas.template.layout === 'bmc' ? (
        <BusinessModelCanvas
          specs={canvas.template.blocks}
          blocks={blocks}
          onChangeBlock={changeBlock}
        />
      ) : (
        <StubCanvas specs={canvas.template.blocks} blocks={blocks} onChangeBlock={changeBlock} />
      )}
    </PageContainer>
  )
}
